import { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useAuth, apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { translations } from "../constants/translations";
import { useTodayAttendance } from "./useTodayAttendance";

const formatName = (fullName) => {
  if (!fullName) return "";
  return fullName.trim().split(" ").slice(0, 3).join(" ");
};

// Initials of the confirmed name only — empty (no invented letters) when the
// profile carries no name.
const initialsOf = (fullName) => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const useProfileData = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { profile, profileLoading } = useProfile();

  const t = (key) => translations[key]?.[language] || translations[key]?.en || key;

  const [profilePicture, setProfilePicture] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });
  const [showReminderModal, setShowReminderModal] = useState(true); // Always show for now
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
    isLargeDesktop: window.innerWidth > 1440,
  });

  const fileInputRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Attendance reminder ────────────────────────────────────────────────────
  const infinityCode = profile?.infinityCode;
  const {
    hasTodayCheckIn,
    hasTodayCheckOut,
    checkInTime,
    checkOutTime,
    isLoading: isLoadingAttendance,
    loadFailed: attendanceLoadFailed,
  } = useTodayAttendance(infinityCode);

  // ── Show reminder modal on page load ──────────────────────
  useEffect(() => {
    // Only a real answer can say "not checked in" — a failed request says
    // nothing about today's attendance.
    if (!isLoadingAttendance && !attendanceLoadFailed && !hasTodayCheckIn) {
      const hasSeenReminder = sessionStorage.getItem("attendanceReminderShown");
      if (!hasSeenReminder) {
        setShowReminderModal(true);
        sessionStorage.setItem("attendanceReminderShown", "true");
      }
    }
  }, [isLoadingAttendance, attendanceLoadFailed, hasTodayCheckIn]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        height: window.innerHeight,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024 && width <= 1440,
        isLargeDesktop: width > 1440,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });

  const closeAlert = () => setAlertModal((prev) => ({ ...prev, open: false }));

  const handleLogout = () => {
    try { logout(); navigate("/login"); }
    catch { navigate("/login"); }
  };

  // Identity comes only from the server-confirmed profile (the page is not
  // rendered before SessionGate has it) — never from stored or invented names.
  const nameEn = profile?.employeeName || profile?.employeeArabicName || "";
  const nameAr = profile?.employeeArabicName || profile?.employeeName || "";
  const position = profile?.position ?? profile?.employeeType ?? "--";

  const loadProfilePicture = useCallback(async () => {
    try {
      if (!infinityCode) { setProfilePicture(null); return; }

      // apiFetch: if the server is unreachable the photo loads once it is
      // back, instead of staying empty until the page is reopened.
      const response = await apiFetch(`${BASE_URL}/employees/pic/${infinityCode}`, {
        headers: { Accept: "image/*" },
        mode: "cors",
      });

      if (response.ok) {
        const blob = await response.blob();
        setProfilePicture(blob.size > 0 ? URL.createObjectURL(blob) : null);
      } else {
        setProfilePicture(null);
      }
    } catch {
      setProfilePicture(null);
    }
  }, [BASE_URL, infinityCode]);

  const handlePhotoUpload = async (file) => {
    if (!file.type.startsWith("image/")) {
      showAlert(t("invalidFileTitle"), t("invalidFileBody"), "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showAlert(t("fileTooLargeTitle"), t("fileTooLargeBody"), "warning");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await apiFetch(`${BASE_URL}/employees/pic/load/${infinityCode}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        await loadProfilePicture();
        showAlert(t("photoUpdatedTitle"), t("photoUpdatedBody"), "success");
      } else {
        // The server's own (bilingual) explanation when it gives one —
        // never a status code or a raw response body.
        const body = await response.json().catch(() => null);
        const serverMessage = body?.messages?.[language] || null;
        showAlert(t("photoUploadFailedTitle"), serverMessage || t("photoUploadFailedBody"), "error");
      }
    } catch (error) {
      // ConnectionError already carries a clear bilingual message.
      showAlert(t("photoUploadFailedTitle"), error?.isConnectionError ? error.message : t("photoUploadFailedBody"), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handlePhotoUpload(file);
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  useEffect(() => {
    loadProfilePicture();
  }, [loadProfilePicture]);

  useEffect(() => {
    return () => {
      if (profilePicture?.startsWith("blob:")) URL.revokeObjectURL(profilePicture);
    };
  }, [profilePicture]);

  // ── Derived employee data ──────────────────────────────────────────────────
  const employeeData = {
    id: profile?.infinityCode || "--",
    name: formatName(language === "ar" ? nameAr : nameEn),
    position,
    department: position,
    email: profile?.employeeEmail || "--",
    phone: profile?.employeePhone ? "+20" + profile.employeePhone : "--",
    address: profile?.address || profile?.location || "--",
    joinDate: profile?.hiringDate || null,
    employeeId: profile?.clientCode || profile?.infinityCode || "--",
    manager: profile?.firstLineManagerName || "--",
    status: profile?.employeeState || "--",
    avatar: initialsOf(nameEn),
    birthDate: profile?.DoB || null,
    nationalId: profile?.nationalId || "--",
    emergencyContact: profile?.emergencyPhoneNumber ? "+20" + profile.emergencyPhoneNumber : "--",
    workLocation: profile?.location || "--",
    languages: [t("noLanguagesYet")],
    certifications: [t("noCertificationsYet")],
    // No projects/performance API exists yet — nothing is invented here.
    projects: [],
  };

  const performanceData = null;

  const recentActivities = [];

  return {
    // theme / lang
    theme, language, isRTL, t,
    // screen
    screenSize,
    // state
    profilePicture, isUploading,
    activeTab, setActiveTab,
    alertModal, closeAlert,
    showReminderModal, setShowReminderModal,
    // refs
    fileInputRef,
    // handlers
    handleFileSelect, triggerFileInput, handleLogout,
    // data
    employeeData, performanceData, recentActivities,
    // attendance
    hasTodayCheckIn, hasTodayCheckOut, checkInTime, checkOutTime, isLoadingAttendance, attendanceLoadFailed,
    // profile fetch state
    profileLoading,
  };
};

export default useProfileData;